import "@fontsource-variable/manrope";
import "@fontsource-variable/jetbrains-mono";
import "./styles.css";
import type { AnalyzeFailure, AnalyzeSuccess, ClientBootstrap, MediaFormat, PlatformPublic } from "../shared/types";
import {
  FfmpegBusyError,
  isFfmpegJobActive,
  mergeRemoteTracks,
  runLocalJob,
  terminateFfmpeg,
  transcodeRemoteAudioToMp3,
  type JobResult,
  type LocalJob,
  type ToolkitMode,
} from "./ffmpeg-tools";

declare global {
  interface Window {
    fetchora?: ClientBootstrap;
    adsbygoogle?: unknown[];
  }
}

const bootstrap: ClientBootstrap = (() => {
  const fallback: ClientBootstrap = {
    ads: { enabled: false },
    engineConfigured: false,
    downloadsEnabled: false,
    platformCount: 0,
    origin: window.location.origin,
    platformHints: [],
  };
  try {
    const raw = document.getElementById("fetchora-bootstrap")?.textContent;
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ClientBootstrap>;
    return {
      ...fallback,
      ...parsed,
      ads: parsed.ads ?? fallback.ads,
      platformHints: Array.isArray(parsed.platformHints) ? parsed.platformHints : [],
    };
  } catch {
    return fallback;
  }
})();

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function svgIcon(name: "download" | "alert" | "check" | "arrow" | "play" | "video" | "audio" | "image" | "gif"): SVGSVGElement {
  const paths: Record<string, string> = {
    download: '<path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 20h14"/>',
    alert: '<path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.3 3.7 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    arrow: '<path d="M5 12h14"/><path d="m14 7 5 5-5 5"/>',
    play: '<path d="m8 5 11 7-11 7V5Z"/>',
    video: '<path d="M15 10l4.6-2.3A1 1 0 0 1 21 8.6v6.8a1 1 0 0 1-1.4.9L15 14"/><rect x="3" y="6" width="12" height="12" rx="2"/>',
    audio: '<path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.5"/><path d="m21 15-5-5L5 20"/>',
    gif: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 9H7a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2v-2H7"/><path d="M12 9v6M15 15V9h3"/>',
  };
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.7");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = paths[name] ?? paths.arrow ?? "";
  return svg;
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : value >= 10 ? 1 : 2)} ${units[unit]}`;
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return "";
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}` : `${minutes}:${String(secs).padStart(2, "0")}`;
}

function toast(message: string, kind: "info" | "error" = "info"): void {
  const region = document.getElementById("toast-region");
  if (!region) return;
  const item = h("div", `toast toast-${kind}`, message);
  region.append(item);
  window.setTimeout(() => item.remove(), 4_500);
}

function normalizeHost(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
}

function detectPlatform(value: string): { id: string; name: string; seoSlug?: string } | null {
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = normalizeHost(url.hostname);
  let best: { id: string; name: string; seoSlug?: string; length: number } | null = null;
  for (const platform of bootstrap.platformHints) {
    for (const rawDomain of platform.domains) {
      const domain = normalizeHost(rawDomain);
      if (host === domain || host.endsWith(`.${domain}`)) {
        if (!best || domain.length > best.length) {
          best = {
            id: platform.id,
            name: platform.name,
            ...(platform.seoSlug ? { seoSlug: platform.seoSlug } : {}),
            length: domain.length,
          };
        }
      }
    }
  }
  return best ? { id: best.id, name: best.name, ...(best.seoSlug ? { seoSlug: best.seoSlug } : {}) } : null;
}

function setFormError(form: HTMLElement, message: string | null): void {
  const error = form.querySelector<HTMLElement>("[data-form-error]");
  if (!error) return;
  error.hidden = !message;
  error.textContent = message ?? "";
}

function setLoading(form: HTMLElement, loading: boolean): void {
  const button = form.querySelector<HTMLButtonElement>("[data-analyze-submit]");
  const label = form.querySelector<HTMLElement>("[data-button-label]");
  if (button) button.disabled = loading;
  if (label) label.textContent = loading ? "Resolving…" : "Analyze link";
  form.toggleAttribute("data-busy", loading);
}

function validatePublicInput(raw: string): string {
  const value = raw.trim();
  if (!value) throw new Error("Paste a public URL first.");
  if (value.length > 8_192) throw new Error("That URL is too long.");
  const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    throw new Error("That does not look like a valid URL.");
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") throw new Error("Only http:// and https:// URLs are accepted.");
  if (!parsed.hostname || (!parsed.hostname.includes(".") && parsed.hostname !== "localhost")) {
    throw new Error("Enter a public hostname, not a single word.");
  }
  return parsed.toString();
}

interface AnalyzerBinding {
  root: HTMLElement;
  form: HTMLFormElement;
  input: HTMLInputElement;
  results: HTMLElement | null;
  detectChip: HTMLElement | null;
  detectText: HTMLElement | null;
  audioMode: HTMLInputElement | null;
  defaultFilter: "all" | "video" | "audio" | "image";
  sequence: number;
  controller: AbortController | null;
  placeholders: string[];
}

const PLACEHOLDERS = [
  "https://www.youtube.com/watch?v=…",
  "https://www.tiktok.com/@creator/video/…",
  "https://www.instagram.com/reel/…",
  "https://www.pinterest.com/pin/…",
  "https://x.com/creator/status/…",
  "https://www.reddit.com/r/…/comments/…",
  "https://cdn.example.com/video.mp4",
  "https://vimeo.com/…",
];

const analyzers: AnalyzerBinding[] = [];

function updateDetection(binding: AnalyzerBinding): void {
  if (!binding.detectText || !binding.detectChip) return;
  const value = binding.input.value.trim();
  if (!value) {
    binding.detectText.textContent = "Any public source";
    binding.detectChip.dataset.detected = "false";
    return;
  }
  const detected = detectPlatform(value);
  binding.detectText.textContent = detected ? `${detected.name} detected` : "Public link";
  binding.detectChip.dataset.detected = detected ? "true" : "false";
  if (detected?.seoSlug) {
    binding.detectChip.setAttribute("data-guide", `/downloader/${detected.seoSlug}`);
    binding.detectChip.title = `Open the ${detected.name} guide`;
  } else {
    binding.detectChip.removeAttribute("data-guide");
    binding.detectChip.removeAttribute("title");
  }
}

function showResultsShell(binding: AnalyzerBinding, loading: boolean): void {
  const shell = binding.results;
  if (!shell) return;
  shell.hidden = false;
  const loadingEl = shell.querySelector<HTMLElement>("[data-results-loading]");
  const content = shell.querySelector<HTMLElement>("[data-results-content]");
  if (loadingEl) loadingEl.hidden = !loading;
  if (content) content.hidden = loading;
}

function reveal(binding: AnalyzerBinding): void {
  const shell = binding.results;
  if (!shell) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  shell.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  const focusTarget = shell.querySelector<HTMLElement>("[data-result-title]");
  window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), reduce ? 0 : 450);
}

function renderError(binding: AnalyzerBinding, failure: AnalyzeFailure): void {
  if (!binding.results) return;
  const content = binding.results.querySelector<HTMLElement>("[data-results-content]");
  if (!content) return;
  content.replaceChildren();
  const card = h("div", "result-error");
  const header = h("div", "result-error-head");
  const icon = h("span", "error-icon");
  icon.append(svgIcon("alert"));
  const headText = h("div");
  headText.append(h("span", "mini-label", `Error · ${failure.error.code}`), h("h2", "result-title", "That link did not produce a file."));
  const title = headText.querySelector("h2");
  if (title) title.setAttribute("data-result-title", "");
  header.append(icon, headText);
  card.append(header, h("p", "error-message", failure.error.message));
  if (failure.error.hint) card.append(h("p", "error-hint", failure.error.hint));

  const actions = h("div", "result-actions");
  const retry = h("button", "button button-quiet", "Try again") as HTMLButtonElement;
  retry.type = "button";
  retry.addEventListener("click", () => {
    const form = binding.form;
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  });
  const toolkit = h("a", "button button-quiet", "Open local toolkit") as HTMLAnchorElement;
  toolkit.href = "/tools";
  actions.append(retry, toolkit);
  card.append(actions);

  const notes = h("div", "error-notes");
  notes.append(h("strong", "", "What Fetchora will not do"));
  const list = h("ul", "plain-list");
  list.append(
    h("li", "", "Invent a media URL when the source refuses access."),
    h("li", "", "Bypass a login, private account, DRM, or region restriction."),
    h("li", "", "Return a thumbnail or manifest as if it were a finished video."),
  );
  notes.append(list);
  card.append(notes);
  content.append(card);
}

function badge(text: string, tone: "good" | "warn" | "neutral" = "neutral"): HTMLElement {
  return h("span", `badge badge-${tone}`, text);
}

function formatDetailLine(format: MediaFormat): string {
  const parts: string[] = [];
  if (format.width && format.height) parts.push(`${format.width}×${format.height}`);
  else if (format.quality) parts.push(format.quality);
  if (format.fps) parts.push(`${Math.round(format.fps)} fps`);
  if (format.sizeBytes) parts.push(formatBytes(format.sizeBytes));
  if (format.videoCodec) parts.push(format.videoCodec.split(".")[0] ?? format.videoCodec);
  return parts.filter(Boolean).join(" · ");
}

function audioLabel(format: MediaFormat): string {
  if (format.kind !== "video") return "";
  if (format.hasAudio === true) return "Audio included";
  if (format.hasAudio === false) return "Video only";
  return "Audio not inspected";
}

function downloadLink(format: MediaFormat): HTMLAnchorElement {
  const link = h("a", "button button-primary format-download") as HTMLAnchorElement;
  link.href = format.downloadPath;
  link.setAttribute("download", "");
  link.rel = "nofollow";
  link.append(svgIcon("download"), document.createTextNode(format.isStream ? "Save manifest" : "Download"));
  const detail = format.sizeBytes ? ` (${formatBytes(format.sizeBytes)})` : "";
  link.setAttribute("aria-label", `${format.isStream ? "Save manifest" : "Download"} ${format.label} ${detail}`);
  return link;
}

function createFormatCard(format: MediaFormat): HTMLElement {
  const card = h("article", "format-option");
  card.dataset.kind = format.kind;
  const top = h("div", "format-option-top");
  const iconWrap = h("span", `format-icon kind-${format.kind}`);
  iconWrap.append(svgIcon(format.kind));
  const main = h("div", "format-main");
  const titleRow = h("div", "format-title-row");
  titleRow.append(h("strong", "", format.label));
  if (format.isStream) titleRow.append(badge("Manifest", "warn"));
  if (format.requiresMerge) titleRow.append(badge("Video only", "warn"));
  else if (format.kind === "video" && format.hasAudio === true) titleRow.append(badge("Audio included", "good"));
  if (format.kind === "video" && format.hasAudio === null) titleRow.append(badge("Audio not inspected"));
  main.append(titleRow);
  const detail = formatDetailLine(format);
  if (detail) main.append(h("span", "format-detail", detail));
  const codecs: string[] = [];
  if (format.videoCodec && format.videoCodec !== "none") codecs.push(format.videoCodec);
  if (format.audioCodec && format.audioCodec !== "none") codecs.push(format.audioCodec);
  if (codecs.length > 1) main.append(h("span", "format-codecs", codecs.join(" / ")));
  top.append(iconWrap, main, downloadLink(format));
  card.append(top);
  return card;
}

function renderSuccess(binding: AnalyzerBinding, data: AnalyzeSuccess["data"]): void {
  if (!binding.results) return;
  const content = binding.results.querySelector<HTMLElement>("[data-results-content]");
  if (!content) return;
  content.replaceChildren();

  const header = h("div", "result-header");
  const headerMain = h("div", "result-provider-row");
  const monogram = h("span", "result-monogram", data.provider.name.slice(0, 1));
  monogram.style.setProperty("--platform-accent", data.provider.accent ?? "#b9ff7b");
  const providerText = h("span", "result-provider", data.provider.name);
  const via = h("span", "result-via", data.via === "extractor" ? "Extraction service" : data.via === "direct" ? "Direct media" : "Public metadata");
  headerMain.append(monogram, providerText, via);
  const availability = h("span", `status-chip status-${data.provider.availability}`, data.provider.availability.replace(/-/g, " "));
  header.append(headerMain, availability);

  const title = h("h2", "result-title", data.title || `${data.provider.name} media`);
  title.tabIndex = -1;
  title.setAttribute("data-result-title", "");
  content.append(header, title);

  const metaRow = h("div", "result-meta");
  if (data.author) metaRow.append(h("span", "", `By ${data.author}`));
  if (data.durationSeconds) metaRow.append(h("span", "", formatDuration(data.durationSeconds)));
  if (data.formats.length) metaRow.append(h("span", "", `${data.formats.length} format${data.formats.length === 1 ? "" : "s"}`));
  metaRow.append(h("span", "", `Checked ${new Date(data.fetchedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`));
  content.append(metaRow);

  const body = h("div", "result-body");
  if (data.thumbnailUrl && /^https?:\/\//i.test(data.thumbnailUrl)) {
    const thumb = h("div", "result-thumb");
    const image = h("img") as HTMLImageElement;
    image.src = data.thumbnailUrl;
    image.alt = data.title ? `Preview for ${data.title}` : "Public media preview";
    image.loading = "lazy";
    image.referrerPolicy = "no-referrer";
    image.addEventListener("error", () => thumb.remove(), { once: true });
    thumb.append(image);
    body.append(thumb);
  }
  const text = h("div", "result-description");
  if (data.description) text.append(h("p", "", data.description));
  const source = h("a", "text-link result-source") as HTMLAnchorElement;
  source.href = data.canonicalUrl;
  source.target = "_blank";
  source.rel = "noopener noreferrer nofollow";
  source.textContent = "Open source page";
  text.append(source);
  if (data.provider.seoSlug) {
    const guide = h("a", "text-link", "Platform guide") as HTMLAnchorElement;
    guide.href = `/downloader/${data.provider.seoSlug}`;
    text.append(guide);
  }
  body.append(text);
  content.append(body);

  if (data.notices.length) {
    const noticeBox = h("div", "notice-box");
    for (const notice of data.notices) {
      const row = h("div", "notice-row");
      row.append(svgIcon("alert"), h("p", "", notice));
      noticeBox.append(row);
    }
    content.append(noticeBox);
  }

  if (!data.formats.length) {
    const empty = h("div", "formats-empty");
    empty.append(h("strong", "", "No downloadable formats were returned."));
    empty.append(
      h(
        "p",
        "",
        data.provider.availability === "engine-required"
          ? "This platform needs the extraction service. Metadata alone is not a download."
          : "The source did not expose a directly reachable file. Fetchora will not fabricate one.",
      ),
    );
    const toolkit = h("a", "button button-quiet", "Open the local toolkit") as HTMLAnchorElement;
    toolkit.href = "/tools";
    empty.append(toolkit);
    content.append(empty);
  } else {
    const merge = buildMergeBlock(data);
    if (merge) content.append(merge);

    const toolbar = h("div", "format-toolbar");
    const heading = h("div");
    heading.append(h("span", "eyebrow", "Verified formats"), h("h3", "", "Choose a file"));
    const filters = h("div", "filter-tabs");
    filters.setAttribute("role", "group");
    filters.setAttribute("aria-label", "Filter formats by type");
    const kinds: Array<["all" | "video" | "audio" | "image", string]> = [
      ["all", "All"],
      ["video", "Video"],
      ["audio", "Audio"],
      ["image", "Images"],
    ];
    for (const [kind, label] of kinds) {
      const count = kind === "all" ? data.formats.length : data.formats.filter((format) => (kind === "image" ? ["image", "gif"].includes(format.kind) : format.kind === kind)).length;
      if (!count && kind !== "all") continue;
      const button = h("button", "filter-tab", label) as HTMLButtonElement;
      button.type = "button";
      button.dataset.filter = kind;
      button.append(h("span", "filter-count", String(count)));
      if (kind === "all" || kind === binding.defaultFilter) button.classList.add("active");
      filters.append(button);
    }
    toolbar.append(heading, filters);
    content.append(toolbar);

    const list = h("div", "format-list result-format-list");
    const filtered = (kind: string) => {
      if (kind === "all") return data.formats;
      if (kind === "image") return data.formats.filter((format) => format.kind === "image" || format.kind === "gif");
      return data.formats.filter((format) => format.kind === kind);
    };
    const renderList = (kind: string) => {
      list.replaceChildren();
      const formats = filtered(kind);
      const emptyFilter = h("p", "muted-small", "No formats in this category.");
      if (!formats.length) list.append(emptyFilter);
      for (const format of formats) list.append(createFormatCard(format));
    };
    const preferred = data.mode === "audio" ? "audio" : binding.defaultFilter;
    const initial = preferred !== "all" && filtered(preferred).length ? preferred : "all";
    if (initial !== "all") {
      for (const button of filters.querySelectorAll<HTMLElement>("[data-filter]")) button.classList.toggle("active", button.dataset.filter === initial);
    }
    renderList(initial);
    filters.addEventListener("click", (event) => {
      const target = (event.target as Element).closest<HTMLElement>("[data-filter]");
      if (!target) return;
      for (const button of filters.querySelectorAll<HTMLElement>("[data-filter]")) button.classList.toggle("active", button === target);
      renderList(target.dataset.filter ?? "all");
    });
    content.append(list);
  }

  const foot = h("div", "result-foot");
  foot.append(
    h("span", "", "Download links expire after 20 minutes. Media is streamed, not stored."),
  );
  if (data.provider.verification) foot.append(h("span", "", `Integration: ${data.provider.verification.replace(/-/g, " ")}`));
  content.append(foot);
}

function buildMergeBlock(data: AnalyzeSuccess["data"]): HTMLElement | null {
  if (!bootstrap.downloadsEnabled) return null;
  const video = data.formats
    .filter((format) => format.kind === "video" && format.hasAudio === false && !format.isStream)
    .sort((a, b) => (b.height ?? b.sizeBytes ?? 0) - (a.height ?? a.sizeBytes ?? 0))[0];
  const audio = data.formats
    .filter((format) => format.kind === "audio" && !format.isStream)
    .sort((a, b) => (b.sizeBytes ?? 0) - (a.sizeBytes ?? 0))[0];
  if (!video || !audio) return null;
  const total = (video.sizeBytes ?? 0) + (audio.sizeBytes ?? 0);
  const tooLarge = total > 300 * 1024 * 1024;
  const audioTooLarge = (audio.sizeBytes ?? 0) > 300 * 1024 * 1024;
  const unknown = !video.sizeBytes || !audio.sizeBytes;
  const canMp4 = ["mp4", "m4v", "mov"].includes(video.format.toLowerCase());
  const outputLabel = canMp4 ? "MP4" : "MKV";

  const block = h("div", "merge-block");
  const copy = h("div", "merge-copy");
  copy.append(
    h("span", "eyebrow", "Separate tracks detected"),
    h("h3", "", "Video, MP3, and merged MP4"),
    h(
      "p",
      "",
      tooLarge
        ? `These tracks total more than the 300 MB browser-processing limit. Download them separately and use the local merge tool.`
        : unknown
          ? "One or both track sizes are unknown. MP3 and merge jobs are capped at 300 MB and run with FFmpeg.wasm on your device."
          : `Best video (${video.quality}) + best audio (${audio.quality}) · ${formatBytes(total)}. ${outputLabel} and MP3 are produced locally; source tracks still stream through signed links.`,
    ),
  );

  const controls = h("div", "merge-controls");
  const actions = h("div", "merge-actions");

  const mp3 = h("button", "button button-quiet", "MP3") as HTMLButtonElement;
  mp3.type = "button";
  mp3.dataset.remoteMp3 = "true";
  mp3.dataset.audioUrl = audio.downloadPath;
  mp3.dataset.audioExt = audio.format;
  mp3.dataset.title = data.title ?? "fetchora-media";
  mp3.title = "Download the signed audio track and encode MP3 locally with FFmpeg.wasm.";
  mp3.disabled = audioTooLarge;

  const merge = h("button", "button button-primary", tooLarge ? "Too large" : outputLabel) as HTMLButtonElement;
  merge.type = "button";
  merge.dataset.remoteMerge = "true";
  merge.dataset.videoUrl = video.downloadPath;
  merge.dataset.audioUrl = audio.downloadPath;
  merge.dataset.videoExt = video.format;
  merge.dataset.audioExt = audio.format;
  merge.dataset.title = data.title ?? "fetchora-media";
  merge.title = `Merge the video and audio tracks locally into ${outputLabel}.`;
  merge.disabled = tooLarge;

  const videoLink = h("a", "button button-quiet", "Video") as HTMLAnchorElement;
  videoLink.href = video.downloadPath;
  videoLink.setAttribute("download", "");
  videoLink.rel = "nofollow";
  videoLink.title = "Download the video-only stream. It does not contain the separate audio track.";
  videoLink.setAttribute("aria-label", `Download video-only ${video.label}`);

  actions.append(mp3, merge, videoLink);

  const local = h("a", "text-link", "Open the local merge tool") as HTMLAnchorElement;
  local.href = "/tools/video-converter";
  const status = h("p", "merge-status");
  status.hidden = true;
  controls.append(actions, local, status);
  block.append(copy, controls);
  return block;
}

async function handleRemoteMerge(button: HTMLButtonElement): Promise<void> {
  if (isFfmpegJobActive()) {
    toast("Another FFmpeg job is already running. Wait for it to finish.", "error");
    return;
  }
  const block = button.closest<HTMLElement>(".merge-block");
  const status = block?.querySelector<HTMLElement>(".merge-status");
  const videoUrl = button.dataset.videoUrl;
  const audioUrl = button.dataset.audioUrl;
  const videoExt = button.dataset.videoExt ?? "mp4";
  const audioExt = button.dataset.audioExt ?? "m4a";
  const title = button.dataset.title ?? "fetchora-media";
  if (!videoUrl || !audioUrl) return;
  button.disabled = true;
  const original = button.textContent ?? "MP4";
  button.textContent = "Merging…";
  if (status) {
    status.hidden = false;
    status.textContent = "Preparing FFmpeg.wasm. The first run downloads about 31 MB.";
  }
  try {
    const result = await mergeRemoteTracks(
      { url: videoUrl, ext: videoExt, title },
      { url: audioUrl, ext: audioExt, title },
      {
        onStatus: (label) => {
          if (status) status.textContent = label;
        },
        onProgress: (ratio) => {
          if (status) status.textContent = `${status.dataset.stage ?? "Working"} · ${Math.round(ratio * 100)}%`;
        },
      },
    );
    const extension = result.filename.split(".").pop()?.toUpperCase() ?? "MP4";
    if (status) {
      status.textContent = `Merged ${formatBytes(result.blob.size)} locally as ${extension}. Your download should begin.`;
    }
    triggerBlobDownload(result.blob, result.filename);
    toast(`Merge complete. The ${extension} file was created in your browser.`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "The merge failed.";
    if (status) status.textContent = message;
    toast(message, "error");
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
}

async function handleRemoteMp3(button: HTMLButtonElement): Promise<void> {
  if (isFfmpegJobActive()) {
    toast("Another FFmpeg job is already running. Wait for it to finish.", "error");
    return;
  }
  const block = button.closest<HTMLElement>(".merge-block");
  const status = block?.querySelector<HTMLElement>(".merge-status");
  const audioUrl = button.dataset.audioUrl;
  const audioExt = button.dataset.audioExt ?? "webm";
  const title = button.dataset.title ?? "fetchora-media";
  if (!audioUrl) return;
  button.disabled = true;
  const original = button.textContent ?? "MP3";
  button.textContent = "Converting…";
  if (status) {
    status.hidden = false;
    status.textContent = "Downloading the signed audio track. FFmpeg.wasm will encode MP3 locally.";
  }
  try {
    const result = await transcodeRemoteAudioToMp3(
      { url: audioUrl, ext: audioExt, title },
      {
        onStatus: (label) => {
          if (status) status.textContent = label;
        },
        onProgress: (ratio) => {
          if (status) status.textContent = `${status.dataset.stage ?? "Working"} · ${Math.round(ratio * 100)}%`;
        },
      },
    );
    if (status) {
      status.textContent = `MP3 ready (${formatBytes(result.blob.size)}). Your download should begin.`;
    }
    triggerBlobDownload(result.blob, result.filename);
    toast("MP3 conversion complete. The file was encoded in your browser.");
  } catch (error) {
    const message = error instanceof Error ? error.message : "The MP3 conversion failed.";
    if (status) status.textContent = message;
    toast(message, "error");
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
}

const blobUrls = new Set<string>();
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  blobUrls.add(url);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    blobUrls.delete(url);
  }, 60_000);
}

async function analyze(binding: AnalyzerBinding): Promise<void> {
  setFormError(binding.form, null);
  let url: string;
  try {
    url = validatePublicInput(binding.input.value);
    binding.input.value = url;
    updateDetection(binding);
  } catch (error) {
    setFormError(binding.form, error instanceof Error ? error.message : "Enter a valid public URL.");
    binding.input.focus();
    return;
  }

  binding.sequence += 1;
  const sequence = binding.sequence;
  binding.controller?.abort();
  binding.controller = new AbortController();
  const controller = binding.controller;
  const timeout = window.setTimeout(() => controller.abort("timeout"), 90_000);
  setLoading(binding.form, true);
  showResultsShell(binding, true);
  reveal(binding);

  try {
    const response = await fetch("/api/analyze", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ url, mode: binding.audioMode?.checked ? "audio" : "video" }),
      signal: controller.signal,
      credentials: "same-origin",
    });
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      throw new Error("The server returned an unexpected response. Check that the deployment is configured correctly.");
    }
    const payload = (await response.json()) as AnalyzeSuccess | AnalyzeFailure;
    if (sequence !== binding.sequence) return;
    if (!payload.ok) {
      showResultsShell(binding, false);
      renderError(binding, payload);
      reveal(binding);
      return;
    }
    showResultsShell(binding, false);
    renderSuccess(binding, payload.data);
    binding.results?.querySelectorAll<HTMLElement>("[data-result-title]").forEach((node) => node.setAttribute("tabindex", "-1"));
    reveal(binding);
  } catch (error) {
    if (sequence !== binding.sequence) return;
    if (controller.signal.aborted && controller.signal.reason === "timeout") {
      showResultsShell(binding, false);
      renderError(binding, {
        ok: false,
        error: {
          code: "upstream_timeout",
          message: "The analysis took longer than 90 seconds and was cancelled.",
          hint: "Try again, or use a direct media URL. The extraction service may be slow or unavailable.",
        },
      });
      reveal(binding);
        return;
    }
    if (controller.signal.aborted) return;
    showResultsShell(binding, false);
    renderError(binding, {
      ok: false,
      error: {
        code: "upstream_error",
        message: error instanceof Error ? error.message : "The network request failed.",
        hint: "Check your connection and try again. No result was fabricated.",
      },
    });
    reveal(binding);
  } finally {
    window.clearTimeout(timeout);
    if (sequence === binding.sequence) setLoading(binding.form, false);
  }
}

function setupAnalyzers(): void {
  const forms = Array.from(document.querySelectorAll<HTMLFormElement>("[data-analyze-form]"));
  for (const form of forms) {
    const root = form.closest<HTMLElement>("[data-analyzer]");
    const input = form.querySelector<HTMLInputElement>("[data-url-input]");
    if (!root || !input) continue;
    const binding: AnalyzerBinding = {
      root,
      form,
      input,
      results: document.querySelector<HTMLElement>("[data-results]"),
      detectChip: form.querySelector<HTMLElement>("[data-detect-chip]"),
      detectText: form.querySelector<HTMLElement>("[data-detect-text]"),
      audioMode: form.querySelector<HTMLInputElement>("[data-audio-mode]"),
      defaultFilter: (root.dataset.filter as "all" | "video" | "audio" | "image" | undefined) ?? "all",
      sequence: 0,
      controller: null,
      placeholders: PLACEHOLDERS,
    };
    analyzers.push(binding);
    input.addEventListener("input", () => updateDetection(binding));
    input.addEventListener("focus", () => {
      input.dataset.userEdited = "true";
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void analyze(binding);
    });
    const paste = form.querySelector<HTMLButtonElement>("[data-paste-button]");
    paste?.addEventListener("click", async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (!text.trim()) {
          toast("The clipboard is empty.");
          return;
        }
        binding.input.value = text.trim();
        updateDetection(binding);
        binding.input.focus();
      } catch {
        toast("Clipboard access was denied. Paste with your keyboard instead.", "error");
        binding.input.focus();
      }
    });
    updateDetection(binding);
  }

  const detectChips = document.querySelectorAll<HTMLElement>("[data-detect-chip]");
  for (const chip of detectChips) {
    chip.addEventListener("click", () => {
      const href = chip.dataset.guide;
      if (href) window.location.href = href;
    });
    chip.style.cursor = "default";
  }

  if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches && analyzers.length) {
    let index = 0;
    window.setInterval(() => {
      index = (index + 1) % PLACEHOLDERS.length;
      for (const binding of analyzers) {
        if (!binding.input.value && document.activeElement !== binding.input && !binding.input.dataset.userEdited) {
          binding.input.placeholder = PLACEHOLDERS[index] ?? PLACEHOLDERS[0] ?? "";
        }
      }
    }, 5_000);
  }
}

const FORMAT_OPTIONS: Record<ToolkitMode, Array<{ value: string; label: string }>> = {
  "convert-video": [
    { value: "mp4", label: "MP4 · H.264 + AAC" },
    { value: "webm", label: "WebM · VP9 + Opus" },
  ],
  "convert-audio": [
    { value: "mp3", label: "MP3 · LAME" },
    { value: "m4a", label: "M4A · AAC" },
    { value: "wav", label: "WAV · PCM" },
    { value: "ogg", label: "OGG · Vorbis" },
  ],
  "extract-audio": [
    { value: "mp3", label: "MP3 · LAME" },
    { value: "wav", label: "WAV · PCM" },
  ],
  merge: [
    { value: "mkv", label: "MKV · stream copy" },
    { value: "mp4", label: "MP4 · stream copy" },
  ],
  inspect: [],
};

const ACCEPT: Record<ToolkitMode, string> = {
  "convert-video": "video/*",
  "convert-audio": "audio/*",
  "extract-audio": "video/*",
  merge: "video/*",
  inspect: "video/*,audio/*,image/*",
};

const OPERATION_LABELS: Record<ToolkitMode, string> = {
  "convert-video": "Convert video",
  "convert-audio": "Convert audio",
  "extract-audio": "Extract audio",
  merge: "Merge tracks",
  inspect: "Inspect metadata",
};

function setupToolkit(panel: HTMLElement): void {
  const operation = panel.querySelector<HTMLSelectElement>("[data-toolkit-operation]");
  const format = panel.querySelector<HTMLSelectElement>("[data-toolkit-format]");
  const fileInput = panel.querySelector<HTMLInputElement>("[data-toolkit-file]");
  const secondaryInput = panel.querySelector<HTMLInputElement>("[data-toolkit-secondary]");
  const secondaryDropzone = panel.querySelector<HTMLElement>("[data-secondary-dropzone]");
  const trimRow = panel.querySelector<HTMLElement>("[data-trim-row]");
  const startInput = panel.querySelector<HTMLInputElement>("[data-toolkit-start]");
  const endInput = panel.querySelector<HTMLInputElement>("[data-toolkit-end]");
  const fileTitle = panel.querySelector<HTMLElement>("[data-file-title]");
  const fileNote = panel.querySelector<HTMLElement>("[data-file-note]");
  const secondaryTitle = panel.querySelector<HTMLElement>("[data-secondary-file-title]");
  const warning = panel.querySelector<HTMLElement>("[data-file-warning]");
  const runButton = panel.querySelector<HTMLButtonElement>("[data-toolkit-run]");
  const cancelButton = panel.querySelector<HTMLButtonElement>("[data-toolkit-cancel]");
  const runLabel = panel.querySelector<HTMLElement>("[data-run-label]");
  const stageEmpty = panel.querySelector<HTMLElement>("[data-stage-empty]");
  const stageProgress = panel.querySelector<HTMLElement>("[data-stage-progress]");
  const stageResult = panel.querySelector<HTMLElement>("[data-stage-result]");
  const stageError = panel.querySelector<HTMLElement>("[data-stage-error]");
  const progressLabel = panel.querySelector<HTMLElement>("[data-progress-label]");
  const progressPercent = panel.querySelector<HTMLElement>("[data-progress-percent]");
  const progressFill = panel.querySelector<HTMLElement>("[data-progress-fill]");
  const progressBar = panel.querySelector<HTMLElement>("[data-progressbar]");
  const stageNote = panel.querySelector<HTMLElement>("[data-stage-note]");
  const resultTitle = panel.querySelector<HTMLElement>("[data-result-title]");
  const resultMeta = panel.querySelector<HTMLElement>("[data-result-meta]");
  const resultDownload = panel.querySelector<HTMLAnchorElement>("[data-result-download]");
  const metadataOutput = panel.querySelector<HTMLElement>("[data-metadata-output]");
  const errorText = panel.querySelector<HTMLElement>("[data-stage-error-text]");
  if (!operation || !format || !fileInput || !runButton) return;

  let currentResultUrl: string | null = null;
  let running = false;

  const setMode = (mode: ToolkitMode): void => {
    operation.value = mode;
    const options = FORMAT_OPTIONS[mode];
    format.replaceChildren();
    for (const option of options) {
      const el = document.createElement("option");
      el.value = option.value;
      el.textContent = option.label;
      format.append(el);
    }
    const formatField = format.closest<HTMLElement>(".field");
    if (formatField) formatField.hidden = options.length === 0;
    if (trimRow) trimRow.hidden = mode === "inspect" || mode === "merge";
    if (secondaryDropzone) secondaryDropzone.hidden = mode !== "merge";
    fileInput.accept = ACCEPT[mode];
    if (secondaryInput) secondaryInput.accept = "audio/*";
    if (runLabel) runLabel.textContent = mode === "inspect" ? "Inspect file" : mode === "merge" ? "Merge locally" : "Process locally";
    if (fileNote) {
      fileNote.textContent =
        mode === "convert-video"
          ? "Video file · processed locally"
          : mode === "convert-audio"
            ? "Audio file · processed locally"
            : mode === "extract-audio"
              ? "Video file with an audio track"
              : mode === "merge"
                ? "Video-only file · add a separate audio file"
                : "Video, audio, or image · inspect locally";
    }
  };

  const defaultMode = (panel.dataset.defaultMode as ToolkitMode | undefined) ?? "convert-video";
  setMode(FORMAT_OPTIONS[defaultMode] ? defaultMode : "convert-video");
  operation.addEventListener("change", () => setMode(operation.value as ToolkitMode));

  const updateFile = (): void => {
    const file = fileInput.files?.[0];
    if (fileTitle) fileTitle.textContent = file ? file.name : "Choose a media file";
    if (fileNote) fileNote.textContent = file ? `${formatBytes(file.size)} · ${file.type || "unknown type"}` : "Choose a local media file";
    if (warning) {
      warning.hidden = !file || file.size <= 250 * 1024 * 1024;
      warning.textContent = file && file.size > 1_500_000_000
        ? "This file is larger than the 1.5 GB browser limit. Choose a smaller file."
        : file && file.size > 250 * 1024 * 1024
          ? "Large file: browser memory may not be sufficient. Keep the tab open and close other heavy tabs."
          : "";
    }
  };
  fileInput.addEventListener("change", updateFile);
  secondaryInput?.addEventListener("change", () => {
    const file = secondaryInput.files?.[0];
    if (secondaryTitle) secondaryTitle.textContent = file ? file.name : "Choose the audio track";
  });

  const setStage = (stage: "empty" | "progress" | "result" | "error"): void => {
    if (stageEmpty) stageEmpty.hidden = stage !== "empty";
    if (stageProgress) stageProgress.hidden = stage !== "progress";
    if (stageResult) stageResult.hidden = stage !== "result";
    if (stageError) stageError.hidden = stage !== "error";
  };
  const setProgress = (ratio: number, label?: string, note?: string): void => {
    const clamped = Math.max(0, Math.min(1, ratio));
    if (progressFill) progressFill.style.width = `${clamped * 100}%`;
    if (progressPercent) progressPercent.textContent = `${Math.round(clamped * 100)}%`;
    if (progressLabel && label) progressLabel.textContent = label;
    if (progressBar) progressBar.setAttribute("aria-valuenow", String(Math.round(clamped * 100)));
    if (stageNote && note) stageNote.textContent = note;
  };

  runButton.addEventListener("click", async () => {
    if (running) return;
    const file = fileInput.files?.[0];
    if (!file) {
      setStage("error");
      if (errorText) errorText.textContent = "Choose a file first. Fetchora does not download or upload a file for this step.";
      return;
    }
    if (file.size > 1_500_000_000) {
      setStage("error");
      if (errorText) errorText.textContent = "This file exceeds the 1.5 GB browser-processing limit. Use a desktop converter.";
      return;
    }
    const mode = operation.value as ToolkitMode;
    if (FORMAT_OPTIONS[mode].length && !format.value) {
      setStage("error");
      if (errorText) errorText.textContent = "Choose an output format.";
      return;
    }
    if (mode === "merge" && !secondaryInput?.files?.[0]) {
      setStage("error");
      if (errorText) errorText.textContent = "Choose the separate audio track before merging.";
      return;
    }
    if (isFfmpegJobActive()) {
      setStage("error");
      if (errorText) errorText.textContent = "Another FFmpeg job is already running. Wait for it to finish or cancel it.";
      return;
    }

    running = true;
    runButton.disabled = true;
    if (cancelButton) cancelButton.hidden = false;
    if (currentResultUrl) {
      URL.revokeObjectURL(currentResultUrl);
      currentResultUrl = null;
    }
    setStage("progress");
    setProgress(0.01, "Preparing job…", "The first FFmpeg run downloads the pinned WebAssembly core from jsDelivr. Later jobs reuse it.");
    const start = startInput?.value ? Number(startInput.value) : 0;
    const end = endInput?.value ? Number(endInput.value) : 0;
    const job: LocalJob = {
      mode,
      file,
      ...(secondaryInput?.files?.[0] ? { secondary: secondaryInput.files[0] } : {}),
      format: format.value || "mkv",
      ...(Number.isFinite(start) && start > 0 ? { start } : {}),
      ...(Number.isFinite(end) && end > 0 ? { end } : {}),
    };

    try {
      const result: JobResult = await runLocalJob(job, {
        onStatus: (label) => setProgress(Number(progressPercent?.textContent?.replace("%", "") ?? 0) / 100, label),
        onProgress: (ratio) => setProgress(ratio),
        onLog: () => undefined,
      });
      if (result.kind === "report") {
        setStage("result");
        if (resultTitle) resultTitle.textContent = "Metadata report";
        if (resultMeta) resultMeta.textContent = `${file.name} · ${formatBytes(file.size)} · inspected locally`;
        if (resultDownload) resultDownload.hidden = true;
        if (metadataOutput) {
          metadataOutput.hidden = false;
          const report = `File: ${file.name}\nType: ${file.type || "unknown"}\nSize: ${formatBytes(file.size)}\n\n${result.report}`;
          metadataOutput.textContent = report;
        }
      } else {
        currentResultUrl = URL.createObjectURL(result.blob);
        setStage("result");
        if (resultTitle) resultTitle.textContent = "Your file is ready";
        if (resultMeta) resultMeta.textContent = `${result.filename} · ${formatBytes(result.blob.size)} · generated locally`;
        if (resultDownload) {
          resultDownload.hidden = false;
          resultDownload.href = currentResultUrl;
          resultDownload.download = result.filename;
        }
        if (metadataOutput) {
          metadataOutput.hidden = true;
          metadataOutput.textContent = "";
        }
        toast("Processing complete. The file was created in your browser.");
      }
      setProgress(1, "Complete");
    } catch (error) {
      setStage("error");
      const message =
        error instanceof FfmpegBusyError
          ? error.message
          : error instanceof Error
            ? error.message
            : "The browser job failed.";
      console.error("fetchora_local_job_failed", error);
      if (errorText) errorText.textContent = message;
      if (error instanceof Error && /memory|array buffer|allocation/i.test(error.message)) {
        if (errorText) errorText.textContent += " Try a smaller file or close other tabs. Your file was never uploaded.";
      }
    } finally {
      running = false;
      runButton.disabled = false;
      if (cancelButton) cancelButton.hidden = true;
    }
  });

  cancelButton?.addEventListener("click", () => {
    if (!running) return;
    terminateFfmpeg();
    if (progressLabel) progressLabel.textContent = "Cancelling…";
    if (stageNote) stageNote.textContent = "Terminating the FFmpeg worker and releasing its in-memory files.";
    toast("Cancelling the local job. The FFmpeg worker is being terminated.");
  });
}

function setupToolkits(): void {
  for (const panel of document.querySelectorAll<HTMLElement>("[data-toolkit]")) setupToolkit(panel);
}

function setupAds(): void {
  if (!bootstrap.ads.enabled) return;
  window.adsbygoogle = window.adsbygoogle ?? [];
  window.setTimeout(() => {
    for (const ad of document.querySelectorAll<HTMLElement>(".adsbygoogle")) {
      if (ad.dataset.adsbygoogleStatus) continue;
      try {
        window.adsbygoogle?.push({});
      } catch {
        // Ad blockers or a blocked Google script must not break the app.
      }
    }
  }, 200);
}

function setupMobileNav(): void {
  const details = document.querySelector<HTMLDetailsElement>(".mobile-nav");
  if (!details) return;
  details.addEventListener("click", (event) => {
    if ((event.target as Element).closest("a")) details.open = false;
  });
}

function setupGlobals(): void {
  document.addEventListener("click", (event) => {
    const mp3Button = (event.target as Element).closest<HTMLButtonElement>("[data-remote-mp3]");
    if (mp3Button) {
      event.preventDefault();
      void handleRemoteMp3(mp3Button);
      return;
    }
    const mergeButton = (event.target as Element).closest<HTMLButtonElement>("[data-remote-merge]");
    if (mergeButton) {
      event.preventDefault();
      void handleRemoteMerge(mergeButton);
    }
  });
  window.addEventListener("pagehide", () => {
    terminateFfmpeg();
    for (const url of blobUrls) URL.revokeObjectURL(url);
    blobUrls.clear();
  });
}

function init(): void {
  setupAnalyzers();
  setupToolkits();
  setupGlobals();
  setupAds();
  setupMobileNav();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
